import classNames from 'classnames';
import dayjs from 'dayjs';
import { isArray, random } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { REGISTER_ID_SEARCH_PARAM_KEY } from '@/modules/common/constants/search-params.ts';
import {
  useRegisterBanksListLazyQuery,
  useRegisterBanksListQuery,
} from '@/modules/issuer-dashboard/queries/banks.ts';
import { SelectPaginate, SelectPaginateOption } from 'ui';
import { fixNameEnum, goodFormatNumber } from 'ui/utils';
import {
  BankTypeEnum,
  CashMovementType,
  PaymentDirection,
  RegisterBankExtended,
  TransactionType,
} from 'api';

export const mapTransactionTypeToBankType: Partial<Record<TransactionType, BankTypeEnum>> = {
  [TransactionType.Subscription]: BankTypeEnum.Subscription,
  [TransactionType.OpeningBalance]: BankTypeEnum.Subscription,
  [TransactionType.BuyBack]: BankTypeEnum.Redemption,
  [TransactionType.Redemption]: BankTypeEnum.Redemption,
  [TransactionType.Drp]: BankTypeEnum.Dividend,
};

export const mapPaymentDirectionToBankType: Partial<Record<PaymentDirection, BankTypeEnum>> = {
  [PaymentDirection.FundToInvestor]: BankTypeEnum.Redemption,
  [PaymentDirection.InvestorToFund]: BankTypeEnum.Subscription,
  // [PaymentDirection.BankToBank]: BankTypeEnum.Portfolio,
};

export const mapCashMovementDirectionToBankType: Partial<Record<CashMovementType, BankTypeEnum>> = {
  [CashMovementType.FundToInvestor]: BankTypeEnum.Redemption,
  [CashMovementType.InvestorToFund]: BankTypeEnum.Subscription,
  // [PaymentDirection.BankToBank]: BankTypeEnum.Portfolio,
};

type baseProps = {
  clearable?: boolean;
  label?: string;
  formFieldTitle?: string;
  error?: string;
  disabled?: boolean;
  className?: string;
  withBorder?: boolean;
  bankType?: BankTypeEnum;
  needBankType?: BankTypeEnum;
  date_to?: string;
  balanceAtExactTime?: boolean;
  is_overall?: boolean;
  portfolio_id?: number;
  registerId?: number;
  placeholder?: string;
  excludeIds?: number[];
  showBorderBottom?: boolean;
  prefillBank?: boolean;
  needClassId?: number;
  showClassWarning?: boolean;
  hideType?: boolean;
  showAvailableCashInsteadOfTypes?: boolean;
  onOptionsMetaChange?: (meta: { count: number; isFetching: boolean }) => void;
  onSelectedBankChange?: (bank: RegisterBankExtended | null) => void;
  warning?: string;
};

type singleProps = {
  isMulti?: false;
  onChange: (options: SelectPaginateOption<RegisterBankExtended> | null, fieldName: string) => void;
  value?: number;
};

type multiProps = {
  isMulti?: true;
  onChange: (
    options: SelectPaginateOption<RegisterBankExtended>[] | null,
    fieldName: string,
  ) => void;
  value?: number[];
};

type RegisterBanksSelectProps = baseProps & (singleProps | multiProps);

export const RegisterBanksSelect = ({
  error,
  label,
  isMulti = false,
  disabled,
  clearable,
  className,
  withBorder = false,
  value,
  formFieldTitle = '',
  onChange,
  bankType,
  registerId,
  placeholder,
  needBankType,
  excludeIds,
  showBorderBottom = true,
  prefillBank = true,
  needClassId,
  showClassWarning = true,
  hideType = false,
  showAvailableCashInsteadOfTypes = false,
  onSelectedBankChange,
  onOptionsMetaChange,
  warning: externalWarning,
  date_to,
  balanceAtExactTime = false,
  is_overall,
  portfolio_id,
}: RegisterBanksSelectProps) => {
  const [params] = useSearchParams();
  const [firstFilled, setFirstFilled] = useState(false);
  const register_id = +(params.get(REGISTER_ID_SEARCH_PARAM_KEY) || 0);
  const [rand] = useState(random(true) + 1000);
  const balanceDateTo = useMemo(() => {
    if (balanceAtExactTime) {
      return date_to && dayjs(date_to).isValid() ? dayjs(date_to).toISOString() : undefined;
    }
    if (portfolio_id) {
      return dayjs(date_to && dayjs(date_to).isValid() ? date_to : undefined).toISOString();
    }
    if (!date_to || !dayjs(date_to).isValid()) return undefined;
    return dayjs(date_to).endOf('day').toISOString();
  }, [balanceAtExactTime, date_to, portfolio_id]);
  const { fetchNextPage } = useRegisterBanksListLazyQuery(
    {
      page: 1,
      page_size: 10,
      registerId: registerId || register_id,
      bank_type: bankType,
      date_to: balanceDateTo,
      is_overall,
      portfolio_id,
    },
    true,
  );
  const { data: dataBanks, isFetching: isFetchingBanks } = useRegisterBanksListQuery(
    {
      page: 1,
      page_size: 50,
      registerId: registerId || register_id,
      bank_type: bankType,
      date_to: balanceDateTo,
      is_overall,
      portfolio_id,
    },
    //!value && !needBankType,
  );

  const allBanks = useMemo<SelectPaginateOption<RegisterBankExtended>[]>(() => {
    return (dataBanks?.data || [])
      ?.filter(bank => !excludeIds || !excludeIds?.includes(bank.id))
      ?.map(bank => {
        const { bank_account_name, bank_types, bank_currency, total_available_cash } = bank;
        const currency = bank_currency.toUpperCase();
        return {
          label: (
            <span className="flex gap-4 items-center justify-between font-[Inter] text-sm pr-3">
              <span className="flex gap-2 items-center font-[Inter] text-sm text-nowrap">
                <span className="flex gap-1 items-center font-[Inter] text-sm text-nowrap">
                  <span
                    className={classNames(
                      'text-gray-900 whitespace-nowrap overflow-hidden text-ellipsis',
                      {
                        'max-w-[170px]': !hideType,
                      },
                    )}
                  >
                    {bank_account_name}
                  </span>
                  {/* <span className="text-gray-500">({bank_name.toUpperCase()})</span> */}
                </span>
                <span className="text-gray-400">{currency}</span>
              </span>
              {!hideType && !showAvailableCashInsteadOfTypes && (
                <span className="text-gray-600 text-xs">
                  {bank_types?.map(item => fixNameEnum(item)).join(', ')}
                </span>
              )}
              {showAvailableCashInsteadOfTypes && (
                <span className="text-gray-500 text-xs font-[Inter]">
                  {isFetchingBanks
                    ? 'Loading balance…'
                    : `Available cash: ${goodFormatNumber(total_available_cash || 0)} ${currency}`}
                </span>
              )}
            </span>
          ),
          value: bank,
          toString: () => bank_account_name,
        };
      });
  }, [dataBanks?.data, excludeIds, hideType, isFetchingBanks, showAvailableCashInsteadOfTypes]);

  const valueWithLabel = useMemo<
    | SelectPaginateOption<RegisterBankExtended>
    | SelectPaginateOption<RegisterBankExtended>[]
    | null
    | undefined
  >(() => {
    if (!value) return null;

    const options = isArray(value)
      ? allBanks.filter(option => value?.includes(option.value.id))
      : allBanks.find(option => option.value.id === value);

    const result = isArray(options)
      ? options
      : options
      ? ({
          label: options?.label,
          value: options?.value,
        } as SelectPaginateOption<RegisterBankExtended>)
      : null;

    return result;
  }, [allBanks, value]);

  useEffect(() => {
    onOptionsMetaChange?.({
      count: dataBanks?.data?.length || 0,
      isFetching: isFetchingBanks,
    });
  }, [dataBanks?.data?.length, isFetchingBanks, onOptionsMetaChange]);

  useEffect(() => {
    if (!isMulti) {
      onSelectedBankChange?.(
        isFetchingBanks || isArray(valueWithLabel)
          ? null
          : (valueWithLabel?.value as RegisterBankExtended | undefined) || null,
      );
    }
  }, [isFetchingBanks, isMulti, onSelectedBankChange, valueWithLabel]);

  useEffect(() => {
    if (!prefillBank || allBanks.length === 0) return;
    if (!value && !firstFilled) {
      let options =
        needBankType && needClassId
          ? allBanks.find(
              option =>
                option.value.bank_types.includes(needBankType) &&
                option.value.bank_classes?.includes(needClassId),
            )
          : null;
      if (!options) {
        options = needBankType
          ? allBanks.find(option => option.value.bank_types.includes(needBankType))
          : null;
      }
      if (!options) {
        options = needClassId
          ? allBanks.find(option => option.value.bank_classes?.includes(needClassId))
          : null;
      }
      if (!options) {
        options = allBanks[0];
      }

      if (options) {
        setFirstFilled(true);
        const result: SelectPaginateOption<RegisterBankExtended> = {
          label: options?.label,
          value: options?.value,
        };
        onChange &&
          onChange(
            (isMulti ? [result] : result) as unknown as SelectPaginateOption<RegisterBankExtended> &
              SelectPaginateOption<RegisterBankExtended>[],
            '',
          );
      }
    }
  }, [prefillBank, firstFilled, allBanks, needBankType, needClassId, isMulti, onChange, value]);

  const handleAsyncLoadFormOptions = useCallback(
    async (page: number, search?: string) => {
      const dataBanks = await fetchNextPage({ pageParam: page });

      const banks = dataBanks?.data?.pages?.at(-1)?.data || [];

      const normalizedSearch = search?.trim().toLowerCase();
      return banks
        ?.filter(bank => {
          const notExcluded = !excludeIds || !excludeIds.includes(bank.id);

          const matchesSearch =
            !normalizedSearch || bank.bank_account_name?.toLowerCase().includes(normalizedSearch);

          return notExcluded && matchesSearch;
        })
        ?.map(bank => {
          const { bank_account_name, bank_types, bank_currency, total_available_cash } = bank;
          const currency = bank_currency.toUpperCase();
          return {
            label: (
              <span className="flex gap-4 items-center justify-between font-[Inter] text-sm pr-3">
                <span className="flex gap-2 items-center font-[Inter] text-sm text-nowrap">
                  <span className="flex gap-1 items-center font-[Inter] text-sm text-nowrap">
                    <span
                      className={classNames(
                        'text-gray-900 whitespace-nowrap overflow-hidden text-ellipsis',
                        {
                          'max-w-[170px]': !hideType,
                        },
                      )}
                    >
                      {bank_account_name}
                    </span>
                    {/* <span className="text-gray-500">({bank_name.toUpperCase()})</span> */}
                  </span>
                  <span className="text-gray-400">{currency}</span>
                </span>
                {!hideType && !showAvailableCashInsteadOfTypes && (
                  <span className="text-gray-600 text-xs">
                    {bank_types?.map(item => fixNameEnum(item)).join(', ')}
                  </span>
                )}
                {showAvailableCashInsteadOfTypes && (
                  <span className="text-gray-500 text-xs font-[Inter]">
                    Available cash: {goodFormatNumber(total_available_cash || 0)} {currency}
                  </span>
                )}
              </span>
            ),
            value: bank,
            toString: () => bank_account_name,
          };
        });
    },
    [excludeIds, fetchNextPage, hideType, showAvailableCashInsteadOfTypes],
  );

  const warningType = useMemo<string | undefined>(() => {
    if (!value || !needBankType || isFetchingBanks) return;

    const allBanks = dataBanks?.data || [];
    const currentBank =
      isMulti && isArray(value)
        ? allBanks.filter(bank => value?.includes(bank.id))
        : allBanks.find(bank => bank.id === value);

    const types = currentBank
      ? isArray(currentBank)
        ? currentBank?.flatMap(({ bank_types }) => bank_types)
        : currentBank?.bank_types || []
      : [];

    return !types.includes(needBankType)
      ? `You have chosen a bank that does not have a ${needBankType} type`
      : undefined;
  }, [dataBanks?.data, isMulti, needBankType, value, isFetchingBanks]);

  const warningClasses = useMemo<string | undefined>(() => {
    if (!showClassWarning || !value || !needClassId || isFetchingBanks) return;

    const allBanks = dataBanks?.data || [];
    const currentBank =
      isMulti && isArray(value)
        ? allBanks.filter(bank => value?.includes(bank.id))
        : allBanks.find(bank => bank.id === value);

    const classes = currentBank
      ? isArray(currentBank)
        ? currentBank?.flatMap(({ bank_classes }) => bank_classes || [])
        : currentBank?.bank_classes || []
      : [];

    return !classes.includes(needClassId)
      ? `You have chosen a bank that does not have the selected class`
      : undefined;
  }, [dataBanks?.data, isMulti, needClassId, showClassWarning, value, isFetchingBanks]);

  const warning = useMemo<string | undefined>(() => {
    if (warningType && warningClasses) {
      return warningType + ' and the selected class';
    }
    return warningType || warningClasses || externalWarning;
  }, [warningType, warningClasses, externalWarning]);

  return (
    <SelectPaginate
      cacheUniqs={[
        `${rand}`,
        `${label}_${JSON.stringify(excludeIds)}`,
        registerId || register_id,
        bankType,
        needClassId,
        balanceDateTo,
        is_overall,
        portfolio_id,
      ]}
      isClearable={clearable}
      disabled={disabled}
      isMulti={isMulti}
      label={label}
      className={className}
      onAsync={handleAsyncLoadFormOptions}
      onChange={options => onChange(options as any, formFieldTitle)}
      value={valueWithLabel}
      error={error}
      warning={warning}
      withBorder={withBorder}
      placeholder={placeholder}
      showBorderBottom={showBorderBottom}
      isLoading={isFetchingBanks}
    />
  );
};
